import { NavItem, NavListCreateNewButton, Select, SelectProps, useModal } from '@billy/design-system'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'

import { queryClient } from '../../../../config/queryClient'
import { ModalId } from '../../../../enums/modalId'
import { zervantSettingsQueryKey } from '../../../invoicingSettings/constants/zervantSettingsQueryKey'
import { useUpdateZervantSettings } from '../../../invoicingSettings/hooks/useUpdateZervantSettings'
import { useZervantSettings } from '../../../invoicingSettings/hooks/useZervantSettings'
import { ProductUnitOnSaveParams, ProductUnitsModal } from '../ProductUnitsModal'

interface ProductUnitSelectProps extends Omit<SelectProps, 'items' | 'onChange' | 'onSelect' | 'selectedId'> {
  onSelect: (unit: string | undefined) => void
  value: string | undefined
}

type HandleSelect = (id: string | undefined, value: string | undefined) => void

export const ProductUnitSelect = ({ onSelect, value, ...props }: ProductUnitSelectProps): ReactElement => {
  const [isSelectOpen, setIsSelectState] = useState(false)

  const { close, open } = useModal(ModalId.ProductUnitModal)

  const { isLoading, data: zervantSettings } = useZervantSettings()
  const { update, isProcessing } = useUpdateZervantSettings()

  const units: NavItem<string>[] = useMemo(() => {
    if (isLoading || !zervantSettings?.managedFields?.UNITS) {
      return []
    }

    const configuredUnits = zervantSettings.managedFields.UNITS
    // Select resolves its label from the items, so a saved unit that is no longer
    // configured has to be kept in the list or the field renders empty
    const availableUnits = value && !configuredUnits.includes(value) ? [...configuredUnits, value] : configuredUnits

    return availableUnits.map((unit) => ({
      id: unit,
      value: unit,
      children: unit,
    }))
  }, [isLoading, value, zervantSettings?.managedFields?.UNITS])

  const updateProductUnits = useCallback(
    async (params: ProductUnitOnSaveParams) => {
      if (!zervantSettings || !zervantSettings.managedFields || isProcessing) {
        return
      }

      update(
        {
          managedFields: {
            ...zervantSettings.managedFields,
            UNITS: params.units,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(zervantSettingsQueryKey)
          },
        },
      )

      if (params.lastUnitAdded) {
        onSelect(params.lastUnitAdded)
      }

      close()
    },
    [close, isProcessing, onSelect, update, zervantSettings],
  )

  const handleSelect = useCallback<HandleSelect>((id, value) => onSelect(value), [onSelect])

  const handleDropdownOpen = useCallback(() => {
    setIsSelectState(true)
  }, [])

  const handleDropdownClose = useCallback(() => {
    setIsSelectState(false)
  }, [])

  const handleCreateNewProductUnit = useCallback(() => {
    setIsSelectState(false)
    open()
  }, [open])

  return (
    <>
      <Select
        {...props}
        dropdownSize="m"
        items={units}
        dropdownFetching={isLoading}
        isOpen={isSelectOpen}
        onDropdownOpen={handleDropdownOpen}
        onDropdownClose={handleDropdownClose}
        onSelect={handleSelect}
        selectedId={value}
        dropdownFooter={<NavListCreateNewButton onClick={handleCreateNewProductUnit} />}
      />

      <ProductUnitsModal
        id={ModalId.ProductUnitModal}
        onSave={updateProductUnits}
        onClose={close}
        units={zervantSettings?.managedFields?.UNITS ?? []}
      />
    </>
  )
}
